// ============================================================
// BOOKING UTILITIES
// ============================================================

import { Building2, CheckCircle2, Clock, DoorOpen, FileText, Monitor, Users, XCircle } from 'lucide-react';
import { BookingStatus, SpaceType } from '../types/booking.types';

export const formatAmount = (amount: number): string => {
  return `${amount.toLocaleString('fr-FR')} FCFA`;
};

export const formatDate = (isoDate: string): string => {
  if (!isoDate) return '';
  return new Date(isoDate).toLocaleDateString('fr-FR', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
};

export const formatDateRange = (startDate: string, endDate?: string): string => {
  if (!endDate || startDate === endDate) {
    return formatDate(startDate);
  }
  return `${formatDate(startDate)} → ${formatDate(endDate)}`;
};

export const getStatusConfig = (status: BookingStatus) => {
  const statusMap: Record<BookingStatus, { label: string; bg: string; text: string; icon: any }> = {
    PAID: {
      label: 'Payé',
      bg: 'bg-emerald-100',
      text: 'text-emerald-700',
      icon: CheckCircle2,
    },
    CONFIRMED: {
      label: 'Confirmé',
      bg: 'bg-blue-100',
      text: 'text-blue-700',
      icon: CheckCircle2,
    },
    PENDING: {
      label: 'En attente',
      bg: 'bg-slate-100',
      text: 'text-slate-700',
      icon: Clock,
    },
    QUOTE_REQUESTED: {
      label: 'Devis',
      bg: 'bg-amber-100',
      text: 'text-amber-700',
      icon: FileText,
    },
    PAYMENT_PENDING: {
      label: 'Paiement en attente',
      bg: 'bg-orange-100',
      text: 'text-orange-700',
      icon: Clock,
    },
    QUOTED: {
      label: 'Devis envoyé',
      bg: 'bg-amber-100',
      text: 'text-amber-700',
      icon: FileText,
    },
    APPROVED: {
      label: 'Approuvé',
      bg: 'bg-blue-100',
      text: 'text-blue-700',
      icon: CheckCircle2,
    },
    REJECTED: {
      label: 'Rejeté',
      bg: 'bg-red-100',
      text: 'text-red-700',
      icon: XCircle,
    },
    CANCELLED: {
      label: 'Annulé',
      bg: 'bg-slate-100',
      text: 'text-slate-700',
      icon: XCircle,
    },
    COMPLETED: {
      label: 'Terminé',
      bg: 'bg-emerald-100',
      text: 'text-emerald-700',
      icon: CheckCircle2,
    },
    URGENT: {
      label: 'URGENT',
      bg: 'bg-red-100',
      text: 'text-red-700',
      icon: Clock,
    },
    OVERDUE: {
      label: 'EN RETARD',
      bg: 'bg-[#8E0B56]', // SING MAGENTA
      text: 'text-white',
      icon: Clock,
    },
  };

  return statusMap[status];
};

export const getSpaceTypeInfo = (type: SpaceType) => {
  const typeMap = {
    COWORKING: { label: 'Coworking', icon: Monitor },
    MEETING_ROOM: { label: 'Salle de réunion', icon: Users },
    PRIVATE_OFFICE: { label: 'Bureau privatif', icon: DoorOpen },
    EVENT_SPACE: { label: 'Espace Évènementiel', icon: Building2 },
  };

  return typeMap[type];
};

export const isDateInRange = (
  date: Date,
  filter: 'all' | 'today' | '7days' | 'month' | 'year',
  referenceDate: Date = new Date(),
): boolean => {
  if (filter === 'all') return true;

  switch (filter) {
    case 'today':
      return date.toDateString() === referenceDate.toDateString();

    case '7days': {
      const sevenDaysAgo = new Date(referenceDate);
      sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);
      return date >= sevenDaysAgo;
    }

    case 'month':
      return date.getMonth() === referenceDate.getMonth() && date.getFullYear() === referenceDate.getFullYear();

    case 'year':
      return date.getFullYear() === referenceDate.getFullYear();

    default:
      return true;
  }
};
